import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh, useCanAccess } from 'ra-core';
import { poserLesColonnesDaffichage } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur, adresseDuContenu } from '../../fournisseurs/client';

/**
 * **Lire un champ de formulaire SANS le convertir de force.**
 *
 * `FormData.get` rend `string | File | null` : `String(...)` sur un `File` rendrait
 * `[object File]`, ce qu'aucun champ de ce formulaire n'attend — et le lint le dit, à juste
 * titre. Cette fonction **rétrécit au texte** et rend la chaîne vide sur tout le reste, ce qui
 * est le comportement voulu : un champ vide vide la colonne, et vider une colonne déclencheuse
 * retire la structure du site public (F-58).
 */
function texteDuFormulaire(formulaire: FormData, nom: string): string {
  const valeur = formulaire.get(nom);
  return typeof valeur === 'string' ? valeur : '';
}

type Chiffre = { valeur: string; libelle: string };
type Colonnes = {
  logoUrl: string | null;
  nomMarque: string | null;
  phrase: string | null;
  siteUrl: string | null;
  chiffres: Chiffre[];
};
type Structure = {
  id: string;
  role?: string;
  colonnesAffichage?: Colonnes;
  paraitSurLeSite?: boolean;
  colonnesDeclencheusesManquantes?: string[];
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES SIX COLONNES D'AFFICHAGE — F-58, ET CE N'EST PAS UN ÉCRAN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-58 ouvre par là : « **ce n'est pas un écran à part, c'est une partie de la fiche d'une
 * structure.** » D'où un bloc, pas une page, et aucune entrée de menu.
 *
 * **Six colonnes, liste fermée** : logo, nom d'affichage de la marque, phrase au nom de la
 * structure, adresse de son site, et **jusqu'à trois couples chiffre et libellé**. « **Trois
 * déclenchent sa présence** sur le site public. »
 *
 * **Le compte qui voit tout les remplit SEUL. La personne qui tient l'espace n'en lit ni
 * n'en écrit aucune, y compris les siennes : refus explicite**, « confirmé plutôt que
 * supposé » (Q513). Deux verrous et non un : la matrice le cache, **et le contrat ne lui rend
 * pas le champ** — la branche `orgadmin` du `oneOf` porte quatre champs et pas ce bloc.
 * D'où le test sur `colonnesAffichage` plutôt que sur le rôle : **l'écran lit ce qu'il a
 * reçu**, il ne devine pas ce qu'il aurait dû recevoir.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN GESTE « RETIRER DU SITE » N'EXISTE, ET C'EST CE QUE LE BLOC DOIT DIRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-58 : « **AUCUN GESTE « RETIRER DU SITE » N'EXISTE.** Vider une des trois colonnes
 * déclencheuses la retire, **la présence étant dérivée et jamais stockée**. » Et, dans la
 * phrase suivante, l'exigence qui fait vivre ce bloc : « **l'écran dit si la structure paraît
 * sur le site, et QUELLE colonne déclencheuse manque si elle n'y paraît pas.** Sans cela, une
 * présence dérivée est indevinable, et **le seul moyen de savoir serait d'ouvrir le site
 * public**. »
 *
 * Le contrat rend les deux valeurs pour cela — `paraitSurLeSite` et
 * `colonnesDeclencheusesManquantes` —, et elles sont **dérivées côté serveur** : les
 * recalculer ici ferait une seconde règle de présence, qui divergerait le jour où la
 * troisième colonne déclencheuse change.
 *
 * **Aucun aperçu de la page publique.** F-58 : « coût assumé : on vérifie en ouvrant le
 * site. » Et le coût hérité, écrit et non corrigé : « **le nom d'une ancienne cliente reste
 * sur l'accueil tant que personne ne vide ses colonnes.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UNE STRUCTURE À LA FOIS, ET LE LOGO EST UN DÉPÔT DE FICHIER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « **Aucune saisie en masse** : une structure à la fois » — d'où un formulaire sur la fiche
 * et aucune action groupée, que le fournisseur de données refuse par ailleurs.
 *
 * Le logo « est un dépôt de fichier, dont le type réel se contrôle **par signature
 * binaire** et qui **ne peut exécuter de code sur aucune page qui le sert** ». Les deux
 * contrôles sont côté serveur, où ils valent quelque chose : ce champ n'en refait aucun, il
 * transporte le fichier. La route consomme du `multipart/form-data`, et le contrat le
 * déclare **depuis D-068** — sans quoi « aucun front ne pouvait envoyer un fichier ».
 *
 * **`retirerLeLogo` est un champ à part, et non un logo vide.** Un envoi multipart ne
 * transporte que des chaînes (D-072) : l'absence du fichier et la demande de le retirer se
 * lisent pareil sans lui.
 */
export function ColonnesDaffichage() {
  const structure = useRecordContext<Structure>();
  const notifier = useNotify();
  const rafraichir = useRefresh();
  const { canAccess } = useCanAccess({
    resource: 'colonnes-affichage',
    action: 'edit',
  });

  const [logo, setLogo] = useState<File | null>(null);
  const [retirerLeLogo, setRetirerLeLogo] = useState(false);
  const [enCours, setEnCours] = useState(false);

  // **Le bloc ne se rend pas du tout à un `orgadmin`** : ni ouvert, ni grisé, ni vide.
  // « Une absence et un refus se lisent pareil » vaut pour un message d'erreur, pas pour un
  // bloc de formulaire — un champ désactivé dirait qu'il existe un chemin ailleurs.
  if (structure === undefined || structure.colonnesAffichage === undefined) return null;

  const colonnes = structure.colonnesAffichage;

  const enregistrer = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    const formulaire = new FormData(evenement.currentTarget);
    const chiffres: Chiffre[] = [];
    for (let rang = 0; rang < 3; rang += 1) {
      const valeur = texteDuFormulaire(formulaire, `chiffre-${rang}-valeur`).trim();
      const libelle = texteDuFormulaire(formulaire, `chiffre-${rang}-libelle`).trim();
      // **Un couple incomplet ne part pas.** Le contrat exige les deux moitiés : un chiffre
      // sans libellé ne désigne rien, et un libellé sans chiffre n'affiche rien.
      if (valeur !== '' && libelle !== '') chiffres.push({ valeur, libelle });
    }

    setEnCours(true);
    try {
      await deballer(
        poserLesColonnesDaffichage({
          path: { id: structure.id },
          body: {
            ...(logo !== null ? { logo } : {}),
            nomMarque: texteDuFormulaire(formulaire, 'nomMarque'),
            phrase: texteDuFormulaire(formulaire, 'phrase'),
            siteUrl: texteDuFormulaire(formulaire, 'siteUrl'),
            // Les listes voyagent en chaîne : un envoi multipart ne transporte que des
            // chaînes, et le contrat le déclare ainsi depuis D-072.
            chiffres: JSON.stringify(chiffres),
            retirerLeLogo: retirerLeLogo ? 'true' : 'false',
          },
        }),
      );
      setLogo(null);
      setRetirerLeLogo(false);
      notifier('Les colonnes d’affichage sont enregistrées.', { type: 'info' });
      rafraichir();
    } catch (erreur) {
      notifier(lireLErreur(erreur).message, { type: 'error' });
    } finally {
      setEnCours(false);
    }
  };

  const manquantes = structure.colonnesDeclencheusesManquantes ?? [];

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-sm font-medium">Colonnes d’affichage</h3>

      {/* **L'exigence de F-58, servie ici et nulle part ailleurs.** */}
      {structure.paraitSurLeSite === true ? (
        <Alert>
          <AlertTitle>Cette structure paraît sur le site public.</AlertTitle>
          <AlertDescription>
            La présence est dérivée de ces colonnes et n’est jamais stockée. Vider une colonne
            déclencheuse la retire au chargement suivant du site — il n’existe aucun geste
            « retirer du site ».
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertTitle>Cette structure ne paraît pas sur le site public.</AlertTitle>
          <AlertDescription>
            {manquantes.length === 0
              ? 'Aucune colonne déclencheuse n’est signalée manquante par le serveur.'
              : `Colonnes déclencheuses manquantes : ${manquantes.join(', ')}.`}
          </AlertDescription>
        </Alert>
      )}

      {colonnes.logoUrl !== null && (
        <div className="flex items-center gap-3">
          <img
            // Le domaine du contenu est séparé de celui de l'application et sans cookies,
            // D-005. Une adresse déjà absolue n'est pas préfixée.
            src={
              colonnes.logoUrl.startsWith('http')
                ? colonnes.logoUrl
                : `${adresseDuContenu()}${colonnes.logoUrl}`
            }
            alt="Logo de la structure"
            className="h-12 w-auto rounded border"
          />
          <span className="text-muted-foreground text-xs">Logo actuel</span>
        </div>
      )}

      {canAccess ? (
        <form className="flex flex-col gap-4" onSubmit={(e) => void enregistrer(e)}>
          <div className="flex flex-col gap-1">
            <Label htmlFor="logo">Logo</Label>
            <Input
              id="logo"
              name="logo"
              type="file"
              // **La liste est celle que le détecteur du serveur reconnaît, et NON
              // `image/*` — amendé le 20260826, D-102.** `image/*` proposait le SVG, que
              // `rangerLeLogo` refuse par `TYPE_NON_AFFICHABLE` : le champ invitait donc à
              // un dépôt dont l'échec était certain, et l'échec arrivait après l'envoi.
              // **Ce filtre n'est pas la garde** — un navigateur laisse choisir « tous les
              // fichiers » —, il évite de proposer ce que le serveur refusera.
              accept="image/png,image/jpeg,image/webp,image/gif,image/avif,image/tiff,image/heic,image/heif"
              onChange={(e) => setLogo(e.target.files?.[0] ?? null)}
            />
            {colonnes.logoUrl !== null && (
              <label className="mt-1 flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={retirerLeLogo}
                  onChange={(e) => setRetirerLeLogo(e.target.checked)}
                />
                Retirer le logo actuel
              </label>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="nomMarque">Nom d’affichage de la marque</Label>
            <Input id="nomMarque" name="nomMarque" defaultValue={colonnes.nomMarque ?? ''} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="phrase">Phrase au nom de la structure</Label>
            <Textarea id="phrase" name="phrase" defaultValue={colonnes.phrase ?? ''} />
            <span className="text-muted-foreground text-xs">
              Au nom de la STRUCTURE, jamais d’une personne : aucune donnée de personne
              n’entre dans une structure.
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="siteUrl">Adresse de son site</Label>
            <Input id="siteUrl" name="siteUrl" defaultValue={colonnes.siteUrl ?? ''} />
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm">Jusqu’à trois couples chiffre et libellé</legend>
            {[0, 1, 2].map((rang) => (
              <div key={rang} className="flex gap-2">
                <Input
                  name={`chiffre-${rang}-valeur`}
                  placeholder="Chiffre"
                  defaultValue={colonnes.chiffres[rang]?.valeur ?? ''}
                  className="w-32"
                />
                <Input
                  name={`chiffre-${rang}-libelle`}
                  placeholder="Ce que ce chiffre désigne"
                  defaultValue={colonnes.chiffres[rang]?.libelle ?? ''}
                />
              </div>
            ))}
          </fieldset>

          <Button type="submit" disabled={enCours} className="self-start">
            Enregistrer les colonnes
          </Button>
        </form>
      ) : null}
    </section>
  );
}
